// Pure helpers behind useForm (no React): dotted paths, structural equality, zod → field errors.
import type { ZodType } from 'zod'

type AnyRecord = Record<string, any>

/** Read a dotted path ("seo.title", "variants.0.price"). */
export function getPath(obj: unknown, path: string): any {
  return path.split('.').reduce<any>((o, k) => (o === null || o === undefined ? undefined : o[k]), obj)
}
/** Immutable set of a dotted path; creates objects/arrays as needed. */
export function setPath<T>(obj: T, path: string, value: unknown): T {
  const keys = path.split('.')
  const recur = (node: any, i: number): any => {
    const k = keys[i]
    const isIndex = /^\d+$/.test(k)
    const base = node === null || node === undefined ? (isIndex ? [] : {}) : node
    const copy: any = Array.isArray(base) ? [...base] : { ...base }
    copy[k] = i === keys.length - 1 ? value : recur(base[k], i + 1)
    return copy
  }
  return recur(obj, 0)
}

/** Structural equality for form values (plain data: objects, arrays, primitives, Dates). */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime()
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    // Treat '' and null/undefined as the same "empty" for dirty tracking.
    const empty = (v: unknown) => v === '' || v === null || v === undefined
    return empty(a) && empty(b)
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false
  if (Array.isArray(a)) {
    const bb = b as unknown[]
    return a.length === bb.length && a.every((v, i) => deepEqual(v, bb[i]))
  }
  const ka = Object.keys(a as AnyRecord).filter(k => (a as AnyRecord)[k] !== undefined)
  const kb = Object.keys(b as AnyRecord).filter(k => (b as AnyRecord)[k] !== undefined)
  const keys = new Set([...ka, ...kb])
  for (const k of keys) if (!deepEqual((a as AnyRecord)[k], (b as AnyRecord)[k])) return false
  return true
}

/** zod issues → { "path.to.field": "message" } (first message per path). */
export function zodErrors(schema: ZodType, values: unknown): Record<string, string> {
  const r = schema.safeParse(values)
  if (r.success) return {}
  const out: Record<string, string> = {}
  for (const issue of r.error.issues) {
    const key = issue.path.join('.') || '_form'
    if (!out[key]) out[key] = issue.message
  }
  return out
}


/** Keys that mean "the whole form" — the server's zodFields() uses `_`, zodErrors() uses `_form`. */
export const ROOT_ERROR_KEYS = ['_', '_form'] as const

/**
 * Split field errors into the ones a form shows next to a field (`shown` = the exact paths it
 * renders errors for) and the rest (root keys, unknown or unrendered fields), which must be shown
 * some other way.
 */
export function splitFieldErrors(errs: Record<string, string>, shown: ReadonlySet<string>): { fields: Record<string, string>; rest: string[] } {
  const fields: Record<string, string> = {}
  const rest: string[] = []
  for (const [k, msg] of Object.entries(errs)) {
    if (typeof msg !== 'string' || !msg) continue
    const root = (ROOT_ERROR_KEYS as readonly string[]).includes(k)
    if (!root && shown.has(k)) fields[k] = msg
    else rest.push(msg)
  }
  return { fields, rest: [...new Set(rest)] }
}
